














































































































import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';
import countBy from 'lodash/countBy';
import maxBy from 'lodash/maxBy';
import isEmpty from 'lodash/isEmpty';
import Breadcrumb from '@/_shared/components/layout/breadcrumb.component.vue';
import NoResults from '@/_shared/components/globals/no-results.component.vue';
import Service from '@/earn/services/earnings.service';
import EarningsResult from '@/earn/entities/earnings/earnings-result.entity';
import Rep from '@/_shared/entities/rep/rep.entity';
import EarningsDetail from '@/earn/components/earnings/earnings-detail.component.vue';
import CurrencyService from '@/_shared/services/currency.service';
import Currency from '@/_shared/entities/currency.entity';
import EarningsDetailsInterface from '@/earn/interfaces/earnings/earnings-details.interface';
import EarningsDetails from '@/earn/entities/earnings/earnings-details.entity';
import EarningsFooter from '@/earn/components/earnings/earnings-footer.component.vue';
import FilterComponent from '@/_shared/components/globals/filter.component.vue';
import FilterConfig from '@/_shared/entities/filter/filter-config.type';
import InputType from '@/_shared/enums/input-type';
import excelService from '@/_shared/services/excel.service';
import EarningsExportInterface from '@/earn/interfaces/earnings/earnings-export.interface';
import ExcelComponent from '@/_shared/components/globals/excel.component.vue';
import DateService from '@/_shared/services/date.service';
import FrontendPagination from '@/_shared/components/pagination/frontend-pagination.component.vue';

const ALL_MONTHS = 0;
const ALL_YEARS = '';

@Component({
  components: {
    EarningsDetail,
    NoResults,
    Breadcrumb,
    FilterComponent,
    ExcelComponent,
    FrontendPagination,
    EarningsFooter,
  },
})
export default class EarningsView extends Vue {
  earningsResult: EarningsResult = new EarningsResult();
  currencies: Currency[] = [];
  selectedYear: number | string = new Date().getFullYear();
  selectedCurrency: Currency = this.repCurrency;
  filterData: FilterConfig[] = [];
  filteredDetails: EarningsDetailsInterface[] = [];
  yearKey = 'selectedYear';
  currencyKey = 'selectedCurrency';
  currentPage = 1;
  itemsPerPage = 12;
  itemsPerPageOptions = [3, 6, 12, ALL_MONTHS];
  currentPageDetails = [];

  get rep(): Rep {
    return this.$store.state.RepStore.rep;
  }

  get repCurrency(): Currency {
    return this.$store.state.RepStore.currency;
  }

  get showNoResults(): boolean {
    const details = this.earningsResult.details || [];
    return !this.$store.state.LoadingStore.isActive && !details.length;
  }

  get showNoPeriodResults(): boolean {
    const allDetails = this.earningsResult.details || [];
    return !this.$store.state.LoadingStore.isActive
      && !this.filteredDetails.length
      && !!allDetails.length;
  }

  get mappedDetails(): EarningsDetailsInterface[] {
    return this.earningsResult.details.map((detail: EarningsDetails) => ({
      ...detail,
      currency: this.currencies
        .find((currency) => currency.currencyTypeId === detail.amountCurrencyId),
    }));
  }

  get filteredDetailsByMonth(): any {
    const sortedDetails = orderBy(this.filteredDetails, 'payoutDate', 'desc');
    const groupedDetails = groupBy(
      sortedDetails,
      (item) => (item.payoutDate || '').substring(0, 7),
    );
    return Object.values(groupedDetails);
  }

  get sameCurrencyDetailsByMonth(): any {
    const sameCurrencyDetails = this.filterDetailsByCurrency();
    const sortedDetails = orderBy(sameCurrencyDetails, 'payoutDate', 'desc');
    const groupedDetails = groupBy(
      sortedDetails,
      (item) => (item.payoutDate || '').substring(0, 7),
    );
    return Object.values(groupedDetails);
  }

  get allYears(): number[] {
    const allYears = uniq(this.earningsResult.details.map((detail) => {
      const payout = DateService.convertDateToUtc(new Date(detail.payoutDate));
      return payout.getFullYear();
    }));
    return orderBy(allYears, [], ['desc']);
  }

  get filteredGrandTotalEarned(): number {
    return this.filteredDetails
      .reduce((acc, item) => acc + item.totalEarnedAmount, 0);
  }

  get filteredGrandTotalPayout(): number {
    return this.filteredDetails
      .reduce((acc, item) => acc + item.payoutAmount, 0);
  }

  getPreviousMonthDetails(detail: EarningsDetailsInterface): EarningsDetailsInterface[] | null {
    let detailMonthIndex = 0;
    this.sameCurrencyDetailsByMonth
      .forEach((month: EarningsDetailsInterface[], monthIndex: number) => {
        const monthDetail = month
          .find((detailOnMonth) => detailOnMonth.registerId === detail.registerId);
        if (monthDetail) {
          detailMonthIndex = monthIndex;
        }
      });
    return this.sameCurrencyDetailsByMonth[detailMonthIndex + 1];
  }

  getPreviousMonthEarnings(detail: EarningsDetailsInterface): number | null {
    const previousMonthDetails = this.getPreviousMonthDetails(detail);
    if (!previousMonthDetails) return null;
    return previousMonthDetails
      .reduce((acc: number, item: EarningsDetails) => acc + item.totalEarnedAmount, 0);
  }

  getPreviousMonthPayout(detail: EarningsDetailsInterface): number | null {
    const previousMonthDetails = this.getPreviousMonthDetails(detail);
    if (!previousMonthDetails) return null;
    return previousMonthDetails
      .reduce((acc: number, item: EarningsDetails) => acc + item.payoutAmount, 0);
  }

  setEarningsResult(): void {
    this.$store.dispatch('showLoading');
    Service.getResults(this.rep.did)
      .then(this.handleResultsFullfilment)
      .finally(() => {
        this.$store.dispatch('hideLoading');
      });
  }

  async handleResultsFullfilment(response: EarningsResult) {
    this.earningsResult = response;
    await this.setCurrencies();
    await this.loadFilters();
  }

  checkFilterCurrency(filter: any) {
    const currencyToCheck = filter[this.currencyKey] || {};
    const hasCurrencyOnDetails = this.currencies
      .find((currency) => currency.currencyTypeId === currencyToCheck.currencyTypeId);
    if (!hasCurrencyOnDetails) {
      this.checkSelectedCurrency();
      return;
    }
    this.selectedCurrency = currencyToCheck;
  }

  checkSelectedCurrency(): void {
    const hasCurrencyOnDetails = this.currencies
      .find((currency) => currency.currencyTypeId === this.selectedCurrency.currencyTypeId);
    if (!hasCurrencyOnDetails) {
      this.setSelectedCurrencyByIncidence();
    }
  }

  private setSelectedCurrencyByIncidence() {
    const currencyByIncidence = countBy(this.currencies, 'currencyTypeId');
    const mostFrequentCurrencyEntry = maxBy(
      Object.entries(currencyByIncidence),
      (entry) => entry[1],
    ) || [];
    const firstCurrencyId = (this.currencies[0] || {}).currencyTypeId;
    const mostFrequentCurrencyId = mostFrequentCurrencyEntry[0] || firstCurrencyId;
    const mostFrequentCurrency = this.currencies
      .find((currency) => currency.currencyTypeId === Number(mostFrequentCurrencyId));
    this.selectedCurrency = mostFrequentCurrency || this.repCurrency;
  }

  checkFilterYear(filter: any) {
    const yearToCheck = filter[this.yearKey] || ALL_YEARS;
    const hasYearOnDetails = yearToCheck === ALL_YEARS
      || this.allYears.find((year) => year === yearToCheck);
    if (!hasYearOnDetails) {
      this.checkSelectedYear();
      return;
    }
    this.selectedYear = yearToCheck;
  }

  checkSelectedYear(): void {
    const hasYearOnDetails = this.selectedYear === ALL_YEARS
      || this.allYears.find((year) => year === this.selectedYear);
    if (!hasYearOnDetails) {
      this.selectedYear = this.allYears[0];
    }
  }

  setCurrencies(): Promise<void> {
    this.currencies = [];
    const currencies = this.getAllCurrenciesFromDetails();
    const currencyFormatPromises: Promise<Currency>[] = [];
    currencies.forEach((currency) => {
      currencyFormatPromises.push(CurrencyService.getCurrencyFormatById(currency));
    });
    return Promise.all(currencyFormatPromises).then((responses) => {
      this.currencies.push(...responses);
    });
  }

  private getAllCurrenciesFromDetails() {
    const currencies: Set<number> = new Set();
    this.earningsResult.details.forEach((detail) => {
      if (detail.amountCurrencyId) {
        currencies.add(detail.amountCurrencyId);
      }
    });
    return currencies;
  }

  async loadFilters(): Promise<void> {
    const years = this.getYearFilterOptions();
    const currencies = this.getCurrencyFilterOptions();
    this.filterData = [
      new FilterConfig({
        key: this.yearKey,
        label: 'GENERIC.YEAR',
        options: years,
        type: InputType.RADIO,
        selectAll: true,
      }),
      new FilterConfig({
        key: this.currencyKey,
        label: 'GENERIC.CURRENCY',
        options: currencies,
        type: InputType.RADIO,
      }),
    ];

    await this.updateCurrentFilter();
  }

  private getCurrencyFilterOptions() {
    return this.currencies
      .map((currency) => ({ label: currency.description, value: currency })) || [];
  }

  private getYearFilterOptions() {
    const years = this.allYears.map((year) => ({ label: year.toString(), value: year })) || [];
    const currentYear = years.find((year) => year.value === new Date().getFullYear());
    if (currentYear) {
      currentYear.label = `${currentYear.label} (${this.$t('GENERIC.CURRENT_YEAR')})`;
    }
    return years;
  }

  async updateCurrentFilter() {
    const currentFilter = await this.$store.dispatch('getFilterState', this.filterData.map((filter) => filter.key));
    if (isEmpty(currentFilter)) {
      await this.doDefaultFilterSearch();
      return;
    }
    this.doStateFilterSearch(currentFilter);
  }

  private doStateFilterSearch(currentFilter: any) {
    this.checkFilterYear(currentFilter);
    this.checkFilterCurrency(currentFilter);
    this.applyFilter();
  }

  private async doDefaultFilterSearch() {
    this.checkSelectedYear();
    this.checkSelectedCurrency();
    await this.setFilterState();
    this.applyFilter();
  }

  private async setFilterState() {
    await this.$store.dispatch('setFilterState', {
      [this.currencyKey]: this.selectedCurrency,
      [this.yearKey]: this.selectedYear || ALL_YEARS,
    });
  }

  applyFilter(): void {
    this.setFilteredDetails();
    this.setFilterState();
    this.changePage(1);
  }

  setFilteredDetails(): void {
    if (this.selectedYear) {
      this.filteredDetails = this.filterDetailsByYearAndCurrency();
      return;
    }
    this.filteredDetails = this.filterDetailsByCurrency();
  }

  private filterDetailsByCurrency(): EarningsDetailsInterface[] {
    return this.mappedDetails.filter(
      (detail) => detail.amountCurrencyId === this.selectedCurrency.currencyTypeId,
    );
  }

  private filterDetailsByYearAndCurrency(): EarningsDetailsInterface[] {
    return this.mappedDetails.filter(
      (detail) => {
        const utcDate = DateService.convertDateToUtc(new Date(detail.payoutDate));
        return detail.amountCurrencyId === this.selectedCurrency.currencyTypeId
          && utcDate.getFullYear() === this.selectedYear;
      },
    );
  }

  exportToExcel() {
    const excelDetails = this.mapDetailsToExcel();
    const year = this.selectedYear || this.$t('GENERIC.ALL_YEARS');
    const formattedYear = year.toString().replace(/ /g, '_');
    const currency = this.selectedCurrency.description.replace(/ /g, '_');
    const filename = `${this.$t('EARNINGS.TITLE')}_${formattedYear}_${currency}`;
    excelService.export(excelDetails, filename, true, '');
  }

  formatNumberAccordingToCurrency(value = 0) {
    return value.toFixed(this.selectedCurrency.decimalPrecision);
  }

  mapDetailsToExcel(): EarningsExportInterface[] {
    const excelRows: any = [];

    this.filteredDetails.forEach((detail) => {
      const payoutDate = DateService.convertDateToUtc(new Date(detail.payoutDate));
      excelRows.push({
        [this.$t('EARNINGS.PERIOD').toString()]: detail.period,
        [this.$t('EARNINGS.PAYOUT_DATE').toString()]: payoutDate.toLocaleDateString(),
        [this.$t('INSIGHT.MY_EARNINGS.TOTAL_EARNINGS').toString()]: this.formatNumberAccordingToCurrency(detail.totalEarnedAmount),
        [this.$t('EARNINGS.TOTAL_BALANCE_FWD').toString()]: this.formatNumberAccordingToCurrency(detail.totalBalanceForward),
        [this.$t('EARNINGS.TOTAL_PAYOUT').toString()]: this.formatNumberAccordingToCurrency(detail.payoutAmount),
      });
    });

    excelRows.push(...this.getExcelFooter());
    return excelRows;
  }

  getExcelFooter(): any[] {
    const footer = [];
    const totalEarnings = this.formatNumberAccordingToCurrency(this.filteredGrandTotalEarned);
    const payoutAmount = this.formatNumberAccordingToCurrency(this.filteredGrandTotalPayout);
    footer.push({
      [this.$t('EARNINGS.PERIOD').toString()]: '',
      [this.$t('EARNINGS.PAYOUT_DATE').toString()]: '',
      [this.$t('INSIGHT.MY_EARNINGS.TOTAL_EARNINGS').toString()]: '',
      [this.$t('EARNINGS.TOTAL_BALANCE_FWD').toString()]: '',
      [this.$t('EARNINGS.TOTAL_PAYOUT').toString()]: '',
    });
    footer.push({
      [this.$t('EARNINGS.PERIOD').toString()]: this.$t('INSIGHT.MY_COMMISSIONS_GENERAL.GRAND_TOTAL').toString(),
      [this.$t('EARNINGS.PAYOUT_DATE').toString()]: '',
      [this.$t('INSIGHT.MY_EARNINGS.TOTAL_EARNINGS').toString()]: totalEarnings,
      [this.$t('EARNINGS.TOTAL_BALANCE_FWD').toString()]: '',
      [this.$t('EARNINGS.TOTAL_PAYOUT').toString()]: payoutAmount,
    });
    return footer;
  }

  changePage(page: number): void {
    this.currentPage = page;
    this.scrollToTop();
  }

  scrollToTop() {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }, 50);
  }

  setItemsPerPageByWindowSize(): void {
    if (window.innerWidth < 960) {
      this.itemsPerPage = 1;
      return;
    }
    this.itemsPerPage = 12;
  }

  created() {
    this.setEarningsResult();
    this.setItemsPerPageByWindowSize();
  }

  @Watch('itemsPerPage')
  onChangeItemsPerPage() {
    this.scrollToTop();
  }
}
